import Swal from 'sweetalert2';

declare global {
    interface Window {
        cookiefirst_show_settings?: () => void;
        cookiefirst_consent?: (category?: string) => void;
        cookiefirst_accept_all?: () => void;
        cookiefirst_decline_all?: () => void;
        dataLayer?: any[];
    }
}

// Optional helper to open CookieFirst settings safely.
// The CookieFirst script loads asynchronously, so if the user clicks before
// it has finished initializing, poll for a short period before giving up.
export function showCookieSettings(retries = 20, intervalMs = 250): void {
    if (typeof window.cookiefirst_show_settings === 'function') {
        window.cookiefirst_show_settings();
        return;
    }

    if (retries <= 0) {
        console.warn('CookieFirst is not available.');
        return;
    }

    setTimeout(() => showCookieSettings(retries - 1, intervalMs), intervalMs);
}

interface AlertOptions {
    title: string;
    text?: string;
    html?: string;
    footer?: string;
    icon?: 'success' | 'error' | 'warning' | 'info';
    imageUrl?: string;
    imageAlt?: string;
    imageWidth?: number;
    imageHeight?: number;
    confirmButtonText?: string;
    popupClass?: string;
    confirmButtonClass?: string;
    showCloseButton?: boolean;
    onConfirm?: () => void;
}

// SweetAlert2 helper with named parameters
export function showAlert({
    title,
    text,
    html,
    footer,
    icon,
    imageUrl,
    imageAlt,
    imageWidth,
    imageHeight,
    confirmButtonText = 'OK',
    popupClass,
    confirmButtonClass,
    showCloseButton = false,
    onConfirm
}: AlertOptions): Promise<void> {
    return Swal.fire({
        title,
        text,
        html,
        footer,
        icon,
        imageUrl,
        imageAlt,
        imageWidth,
        imageHeight,
        confirmButtonText,
        showCloseButton,
        background: 'var(--clr-white)',
        customClass: {
            popup: popupClass,
            confirmButton: confirmButtonClass ?? 'my-custom-button',
            title: 'swal-title',
        },
    }).then((result) => {
        if (result.isConfirmed && onConfirm) onConfirm();
    });
}

interface ConfirmOptions {
    title: string;
    text?: string;
    html?: string;
    icon?: 'success' | 'error' | 'warning' | 'info' | 'question';
    confirmButtonText?: string;
    cancelButtonText?: string;
}

// SweetAlert2 confirm/cancel dialog helper. Resolves true if the user confirmed.
export function showConfirm({
    title,
    text,
    html,
    icon = 'question',
    confirmButtonText = 'Yes',
    cancelButtonText = 'Cancel'
}: ConfirmOptions): Promise<boolean> {
    return Swal.fire({
        title,
        text,
        html,
        icon,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        background: 'var(--clr-white)',
        customClass: { confirmButton: 'my-custom-button' },
    }).then((result) => result.isConfirmed);
}

const PENDING_POPUP_KEY = 'pendingSuccessPopup';

interface PendingPopup {
    title: string;
    message?: string;
}

// Stash a popup to be shown once the next page navigation has completed.
export function setPendingSuccessPopup(title?: string, message?: string): void {
    if (!title) return;
    const popup: PendingPopup = { title, message };
    sessionStorage.setItem(PENDING_POPUP_KEY, JSON.stringify(popup));
}

// Show the stashed popup (if any) and clear it so it doesn't reappear.
export function showPendingSuccessPopup(): void {
    const raw = sessionStorage.getItem(PENDING_POPUP_KEY);
    if (!raw) return;

    sessionStorage.removeItem(PENDING_POPUP_KEY);

    try {
        const popup: PendingPopup = JSON.parse(raw);
        if (!popup.title) return;

        showAlert({
            title: popup.title,
            html: popup.message,
            icon: 'success',
            confirmButtonText: 'OK',
        });
    } catch {
        // Ignore malformed stored popup data
    }
}

export function IsProduction(): boolean {
    return process.env.NODE_ENV === 'production';
}

// Determine whether a URL points to an external site (different origin),
// so links can open in a new tab only when necessary.
export function isExternalUrl(url?: string | null): boolean {
    if (!url) return false;

    const trimmed = url.trim();

    // Relative paths, hashes, and non-http(s) schemes are treated as internal/non-external.
    if (
        trimmed.startsWith('/') ||
        trimmed.startsWith('#') ||
        trimmed.startsWith('mailto:') ||
        trimmed.startsWith('tel:')
    ) {
        return false;
    }

    try {
        const parsed = new URL(trimmed, window.location.origin);
        return parsed.origin !== window.location.origin;
    } catch {
        return false;
    }
}

// Returns "_blank" for external urls, otherwise "_self" so the link opens in the same tab.
export function getLinkTarget(url?: string | null): '_blank' | '_self' {
    return isExternalUrl(url) ? '_blank' : '_self';
}
